import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Vault from './components/Vault';
import secureStorage from './services/secureStorage';
import extensionBridge from './services/extensionBridge';
import api, { API_BASE } from './services/api';
import { ThemeProvider } from './theme/ThemeContext';
import useBranding from './hooks/useBranding';
import useOrgSessionLimit, { applyEffectiveSessionConfig } from './hooks/useOrgSessionLimit';
import AppShell from './layout/AppShell';
import LoginV2 from './pages/Login';
import Dashboard from './pages/Dashboard';
import ItemDetail from './pages/ItemDetail';
import ItemForm from './pages/ItemForm';
import Account from './pages/Account';
import AccountSecurity from './pages/AccountSecurity';
import PublicShare from './pages/PublicShare';
import InviteAccept from './pages/InviteAccept';
import TwoFactor from './pages/TwoFactor';
import Health from './pages/Health';
import Messages from './pages/Messages';
import Share from './pages/Share';
import AdminLayout from './pages/admin/AdminLayout';
import AdminMembers from './pages/admin/Members';
import AdminDepartments from './pages/admin/Departments';
import AdminRoles from './pages/admin/Roles';
import AdminVaults from './pages/admin/Vaults';
import AdminPolicies from './pages/admin/Policies';
import AdminBranding from './pages/admin/Branding';
import AdminBilling from './pages/admin/Billing';
import AdminAudit from './pages/admin/Audit';
import './styles/tokens.css';
import './ui/ui.css';
import './App.css';
import { withLanguage } from './i18n/language';

function App() {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [secureStorageReady, setSecureStorageReady] = useState(false);
  const { branding } = useBranding();
  const navigate = useNavigate();

  // Initialize secure storage and extension bridge
  const initializeSecureSystems = useCallback(async (token, rfidId) => {
    try {
      // Initialize secure storage with origin-bound encryption
      await secureStorage.initialize(token, rfidId);
      setSecureStorageReady(true);
      
      // Initialize extension bridge for communication with browser extension
      extensionBridge.initialize(token, rfidId);
      
      console.log('Secure systems initialized successfully');
      return true;
    } catch (error) {
      console.error('Failed to initialize secure systems:', error);
      return false;
    }
  }, []);

  // Verificar autenticación al cargar
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken');
      const storedRfidId = localStorage.getItem('rfidId');
      
      if (token && storedRfidId) {
        // Configurar axios con el token
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Verify session is still valid on the server
        try {
          const verifyRes = await axios.post('/api/auth/verify', withLanguage({ token }));
          if (verifyRes.data?.valid) {
            // El timeout efectivo (el de la política de la organización, si manda) viene del servidor.
            applyEffectiveSessionConfig(verifyRes.data.sessionConfig);
            // Initialize secure systems
            const success = await initializeSecureSystems(token, storedRfidId);
            if (success) {
              setIsAuthenticated(true);
            }
          } else {
            // Session invalid on server, clean up
            localStorage.removeItem('authToken');
            localStorage.removeItem('rfidId');
            localStorage.removeItem('sessionTimeout');
            delete axios.defaults.headers.common['Authorization'];
          }
        } catch (err) {
          console.warn('[AUTH] Session verify failed:', err.message);
          localStorage.removeItem('authToken');
          localStorage.removeItem('rfidId');
          localStorage.removeItem('sessionTimeout');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      
      setIsInitializing(false);
    };

    checkAuth();
    
    // Close session on server when browser/tab is closed (only for 'browser' session type)
    // Uses sendBeacon which is the only reliable method during page unload
    const handleBeforeUnload = () => {
      const sessionTimeout = localStorage.getItem('sessionTimeout');
      // Only logout on page unload if session is explicitly configured as 'browser' session
      // For all other types (never, 5m, 1h, 24h, etc.) the session persists across reloads
      if (sessionTimeout === 'browser') {
        const token = localStorage.getItem('authToken');
        if (token) {
          const data = JSON.stringify({ token });
          navigator.sendBeacon(
            `${API_BASE}/api/auth/logout`,
            new Blob([data], { type: 'application/json' })
          );
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [initializeSecureSystems]);

  const handleLoginSuccess = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    const rfidId = localStorage.getItem('rfidId');

    if (token && rfidId) {
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
      // initializeSecureSystems → extensionBridge.initialize() already
      // broadcasts BRIDGE_READY + pushes the session once. Posting it again
      // here previously caused an echo loop with the extension content script
      // and OOM'd the tab.
      await initializeSecureSystems(token, rfidId);
    }

    setIsAuthenticated(true);

    // Si el login vino de /invite/:token (sin sesión, guardado antes de
    // redirigir a /login), vuelve ahí para completar la aceptación.
    const pendingInvite = sessionStorage.getItem('pendingInvite');
    if (pendingInvite) {
      sessionStorage.removeItem('pendingInvite');
      navigate(`/invite/${pendingInvite}`);
      return;
    }

    // Si vino de "crear cuenta" con un plan (?register=1&plan=team|business,
    // ver pages/Login.js), va directa al asistente de crear organización con
    // ese plan preseleccionado.
    const pendingPlan = sessionStorage.getItem('pendingPlan');
    if (pendingPlan) {
      sessionStorage.removeItem('pendingPlan');
      navigate(`/admin?plan=${pendingPlan}`);
    }
  }, [initializeSecureSystems, navigate]);

  const handleLogout = useCallback(async () => {
    // NOTE: We intentionally DO NOT clear the encrypted cache on logout
    // The cache is encrypted with origin-bound encryption, so it can only be
    // decrypted when the same smart card logs in again on the same device/browser.
    // This allows instant loading from cache on re-login.
    
    // Close session on the server FIRST (fire-and-forget, don't block on failure)
    const token = localStorage.getItem('authToken');
    if (token) {
      axios.post(`${API_BASE}/api/auth/logout`, { token })
        .catch(err => console.warn('Failed to close server session:', err.message));
    }
    
    // Destroy secure storage (clears encryption key from memory only)
    secureStorage.destroy();
    
    // Destroy extension bridge
    extensionBridge.destroy();
    
    setIsAuthenticated(false);
    setSecureStorageReady(false);
    
    // Clear auth data from localStorage (but NOT encrypted cache data)
    localStorage.removeItem('authToken');
    localStorage.removeItem('rfidId');
    localStorage.removeItem('sessionTimeout');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // Política de sesión de la organización: cierre local por inactividad y refresco del servidor.
  useOrgSessionLimit(isAuthenticated, handleLogout);

  // Global 401 interceptor — auto logout on any unauthorized response
  // Preserves secure storage (encrypted cache stays intact for re-login)
  const isLoggingOut = useRef(false);
  useEffect(() => {
    // Rutas públicas cuyo 401 es "contraseña del enlace incorrecta", no sesión caducada.
    const NOT_SESSION_401 = [/[/]share[/]public[/]/, /[/]auth[/]login/, /[/]account[/]login/, /[/]devices[/]auth[/]/];
    const handle401 = (error) => {
      const url = error.config?.url || '';
      if (error.response?.status === 401 && NOT_SESSION_401.some((re) => re.test(url))) return Promise.reject(error);
      if (error.response?.status === 401 && !isLoggingOut.current) {
        isLoggingOut.current = true;
        console.warn('[401] Session expired or unauthorized — logging out');
        
        // Destroy secure storage (clears encryption key from memory only, NOT the cache)
        secureStorage.destroy();
        extensionBridge.destroy();
        
        setIsAuthenticated(false);
        setSecureStorageReady(false);
        
        // Clear auth data from localStorage (but NOT encrypted cache data)
        localStorage.removeItem('authToken');
        localStorage.removeItem('rfidId');
        localStorage.removeItem('sessionTimeout');
        delete axios.defaults.headers.common['Authorization'];
        
        // Try to close server session (fire-and-forget, ignore errors)
        const token = error.config?.headers?.Authorization?.replace('Bearer ', '');
        if (token) {
          fetch(`${API_BASE}/api/auth/logout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token })
          }).catch(() => {});
        }
        
        // Reset flag after a short delay to allow re-login
        setTimeout(() => { isLoggingOut.current = false; }, 1000);
      }
      return Promise.reject(error);
    };

    // Install on global axios
    const globalInterceptor = axios.interceptors.response.use(
      (response) => response,
      handle401
    );
    // Install on the api instance
    const apiInterceptor = api.interceptors.response.use(
      (response) => response,
      handle401
    );

    return () => {
      axios.interceptors.response.eject(globalInterceptor);
      api.interceptors.response.eject(apiInterceptor);
    };
  }, []);

  // Show loading while initializing
  if (isInitializing) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>{t('app.initializing')}</p>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <Routes>
        {/* Página pública de un enlace compartido: fuera del shell, sin sesión */}
        <Route path="/s/:id" element={<PublicShare />} />
        {/* Aceptación de invitación: fuera del shell, funciona con o sin sesión */}
        <Route path="/invite/:token" element={<InviteAccept />} />

        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <LoginV2 onLoginSuccess={handleLoginSuccess} allowedMethods={branding.loginMethods} />}
        />

        <Route
          path="/"
          element={isAuthenticated ? <AppShell onLogout={handleLogout} /> : <Navigate to="/login" replace />}
        >
          <Route index element={<Dashboard />} />
          <Route path="vault/new" element={<ItemForm />} />
          <Route path="vault/:id/edit" element={<ItemForm />} />
          <Route path="vault/:id" element={<ItemDetail />} />
          {/* Vault.js: el monolito viejo (lista+detalle+formulario), accesible
              solo por si hace falta comparar contra el diseño nuevo. */}
          <Route path="legacy" element={<Vault onLogout={handleLogout} secureStorageReady={secureStorageReady} />} />
          <Route path="2fa" element={<TwoFactor />} />
          <Route path="health" element={<Health />} />
          <Route path="messages" element={<Messages />} />
          <Route path="share" element={<Share />} />
          <Route path="account" element={<Account onLogout={handleLogout} />} />
          <Route path="account/security" element={<AccountSecurity />} />
          <Route path="admin" element={<AdminLayout />}>
            <Route path=":orgId/members" element={<AdminMembers />} />
            <Route path=":orgId/departments" element={<AdminDepartments />} />
            <Route path=":orgId/roles" element={<AdminRoles />} />
            <Route path=":orgId/vaults" element={<AdminVaults />} />
            <Route path=":orgId/policies" element={<AdminPolicies />} />
            <Route path=":orgId/branding" element={<AdminBranding />} />
            <Route path=":orgId/billing" element={<AdminBilling />} />
            <Route path=":orgId/audit" element={<AdminAudit />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </ThemeProvider>
  );
}

export default App;
